import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDebounce } from '@asd-stan/helpers/customHooks';
import { PaginationCollectionController } from '@asd-stan/helpers/pagination-collection-controller';
import { ReactComponent as Search } from '@assets/icons/search.svg';
import { TableFilter } from '@components/content-table/table-control/table-filter/table-filter';
import { Input } from '@components/input/input';
import { Flex } from '@components/utility/flex';

import { StyledTableControl, TableSelect } from './table-control.styled';
import { useLocation, useNavigate } from "react-router-dom";

interface TableControlProps<T extends {}> {
	pageSize: number;
	onPageSizeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
	paginator: PaginationCollectionController<any, any>;
	minSearchLength?: number;
	filterForm?: React.ReactNode;
	filterValues?: T;
	filterEmptyValues?: T;
	onFilterSubmit?(values: T): void;
	filtersDisabled?: boolean;
	hideFilter?: boolean;
	hideSearch?: boolean;
	onFilterOrSearchChange?(): void;
}

const pageSizeOptions = [10, 20, 50, 100];

export const TableControl = observer(
	<T extends {}>({
		pageSize,
		onPageSizeChange,
		paginator,
		minSearchLength = 3,
		filterForm,
		filterValues,
		filterEmptyValues,
		onFilterSubmit: handleFilterSubmit,
		filtersDisabled,
		hideFilter,
		hideSearch,
		onFilterOrSearchChange: handleFilterOrSearchChange,
	}: TableControlProps<T>) => {
		const location = useLocation();
		const queryParams = new URLSearchParams(location.search);
		const navigate = useNavigate();

		const [search, setSearch] = useState<string>(queryParams.get('search') ?? '');
		const { t } = useTranslation();

		const handleSearch = useDebounce(
			() => {
				if (search.length > 0) {
					queryParams.set('search', search);
				} else {
					queryParams.delete('search');
				}

				queryParams.delete('page');
				navigate({ search: queryParams.toString() }, { replace: true });

				paginator?.addFilter(search.length > 0 ? { search } : { search: null });
			},
			500
		);

		const isFirstRender = useRef(true);
		useEffect(() => {
			if (isFirstRender.current) {
				return;
			}

			if (search.length === 0 || search.length >= minSearchLength) {
				handleSearch();
				handleFilterOrSearchChange?.();
			}
		}, [search]);

		return (
			<StyledTableControl>
				{hideFilter ? (
					<div />
				) : (
					<TableFilter
						children={filterForm}
						values={filterValues}
						emptyValues={filterEmptyValues}
						onSubmit={filter => {
							handleFilterSubmit?.(filter);
							handleFilterOrSearchChange?.();
						}}
						disabled={filtersDisabled}
					/>
				)}
				<Flex>
					{!hideSearch && (
						<Input
							className="search"
							value={search}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								const val: string = e.target.value;
								isFirstRender.current = false;
								setSearch(val);

								if (val.length < minSearchLength && val.length !== 0) {
									return;
								}
							}}
							icon={<Search />}
							placeholder={t('user.userList.searchPlaceholder')}
						/>
					)}
					<TableSelect>
						<span>{t('user.userList.searchFilter')}</span>
						<div>
							<select value={pageSize} onChange={onPageSizeChange}>
								{pageSizeOptions.map(pageSizeElement => {
									return (
										<option key={pageSizeElement} value={pageSizeElement}>
											{pageSizeElement}
										</option>
									);
								})}
							</select>
						</div>
					</TableSelect>
				</Flex>
			</StyledTableControl>
		);
	}
);
